import { motion } from "framer-motion";
import {
  FiTarget,
  FiEye,
  FiHeart,
  FiStar,
  FiUsers,
  FiAward,
  FiCheckCircle,
  FiZap,
  FiBarChart,
} from "react-icons/fi";

import HeroBanner from "../components/HeroBanner";
import SectionWrapper from "../components/SectionWrapper";
import aboutHero from "../assets/about-hero.jpg";

const values = [
  { icon: FiZap, title: "Innovation with Purpose" },
  { icon: FiBarChart, title: "Data-Backed Decisions" },
  { icon: FiUsers, title: "Client-First Execution" },
  { icon: FiHeart, title: "Transparency & Integrity" },
  { icon: FiStar, title: "Continuous Improvement" },
];

const whyChoose = [
  "Full-Stack Development & Marketing Under One Roof",
  "Industry-Specific Expertise",
  "SEO & Performance-Focused Approach",
  "Agile Delivery & Ongoing Support",
];

const About = () => {
  return (
    <>
      <HeroBanner
        image={aboutHero}
        title="About GoPhoenix Data Solutions"
        subtitle="A technology-first software and digital marketing company headquartered in the UAE."
      />

      <SectionWrapper>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title mb-6">
              Who <span className="text-primary">We Are</span>
            </h2>
            <p className="text-muted-foreground font-roboto leading-relaxed mb-4">
              GoPhoenix Data Solutions FZ LLC is a technology-first software and
              digital marketing company headquartered in the UAE. We specialize
              in transforming businesses through intelligent software systems,
              data-powered automation, and result-oriented digital marketing
              strategies.
            </p>
            <p className="text-muted-foreground font-roboto leading-relaxed">
              With a team of skilled developers, data scientists, and digital
              marketers, we deliver end-to-end solutions that help businesses
              grow, compete, and dominate in the digital landscape.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 gap-6"
          >
            {[
              { num: "100+", label: "Projects Delivered" },
              { num: "50+", label: "Happy Clients" },
              { num: "7+", label: "Industries Served" },
              { num: "24/7", label: "Support Available" },
            ].map((stat, i) => (
              <div key={i} className="card-hover text-center">
                <span className="text-3xl md:text-4xl font-montserrat font-bold text-primary">
                  {stat.num}
                </span>
                <p className="text-muted-foreground text-sm font-roboto mt-2">
                  {stat.label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>
      </SectionWrapper>

      {/* Mission & Vision */}
      <SectionWrapper className="bg-muted">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="card-hover"
          >
            <FiTarget className="w-12 h-12 text-primary mb-4" />
            <h3 className="font-montserrat font-bold text-2xl mb-4">
              Our Mission
            </h3>
            <p className="text-muted-foreground font-roboto leading-relaxed">
              To empower businesses with scalable digital solutions, actionable
              data insights, and measurable online growth.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="card-hover"
          >
            <FiEye className="w-12 h-12 text-primary mb-4" />
            <h3 className="font-montserrat font-bold text-2xl mb-4">
              Our Vision
            </h3>
            <p className="text-muted-foreground font-roboto leading-relaxed">
              To become a globally trusted technology and digital growth partner
              for startups, enterprises, and government organizations.
            </p>
          </motion.div>
        </div>
      </SectionWrapper>

      {/* Core Values */}
      <SectionWrapper>
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">
            Core <span className="text-primary">Values</span>
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {values.map((val, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="card-hover text-center"
            >
              <val.icon className="w-10 h-10 text-primary mx-auto mb-3" />
              <h4 className="font-montserrat font-semibold text-sm">
                {val.title}
              </h4>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>

      {/* Why Choose Us */}
      <SectionWrapper className="bg-secondary text-secondary-foreground">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-montserrat font-bold text-primary-foreground mb-4">
            Why Choose <span className="text-primary">Us</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {whyChoose.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-4 bg-secondary-foreground/5 p-5 rounded-xl"
            >
              <FiCheckCircle className="w-6 h-6 text-primary flex-shrink-0" />
              <span className="font-roboto text-primary-foreground/90">
                {item}
              </span>
            </motion.div>
          ))}
        </div>
      </SectionWrapper>
    </>
  );
};

export default About;
